import React, { useState } from 'react';
import { Link, useLocation, useNavigate, Outlet } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import useBrandName from '../../hooks/useBrandName';
import { BarChart3, Boxes, Building2, ClipboardList, Factory, FileBarChart, LogOut, Menu, Settings, ShoppingBag, Users, X } from 'lucide-react';

const MENU = [
  { to: '/admin',            label: '대시보드',    icon: BarChart3, exact: true, roles: ['admin','partner','viewer'] },
  { to: '/admin/inventory',  label: '재고 관리',   icon: Boxes, roles: ['admin','partner','viewer'] },
  { to: '/admin/production', label: '생산 현황',   icon: Factory, roles: ['admin','partner','viewer'] },
  { to: '/admin/inquiries',  label: '제작 문의',    icon: ClipboardList, roles: ['admin','partner'] },
  { to: '/admin/report',     label: '리포트',      icon: FileBarChart, roles: ['admin','partner'] },
  { to: '/admin/catalog',    label: '제품 카탈로그', icon: ShoppingBag, roles: ['admin','partner'] },
  { to: '/admin/vendors',    label: '거래처 관리',  icon: Building2, roles: ['admin','partner'] },
  { to: '/admin/users',      label: '사용자 관리',  icon: Users, roles: ['admin'] },
  { to: '/admin/settings',   label: '사이트 설정',  icon: Settings, roles: ['admin'] },
];

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const brandName = useBrandName();
  const location  = useLocation();
  const navigate  = useNavigate();
  const [open, setOpen] = useState(true);

  const handleLogout = () => { logout(); navigate('/'); };

  const isActive = (to: string, exact?: boolean) =>
    exact ? location.pathname === to : location.pathname.startsWith(to);

  const initial = user?.name?.[0]?.toUpperCase() ?? '?';

  return (
    <div className="admin-shell flex h-screen bg-stone-100">
      {/* ── 사이드바 ── */}
      <aside className={`${open ? 'w-56' : 'w-16'} bg-stone-900 text-white flex flex-col transition-all duration-200 flex-shrink-0`}>

        {/* 로고 */}
        <div className="p-4 flex items-center justify-between border-b border-stone-700">
          {open && <Link to="/" className="text-amber-400 font-bold tracking-widest text-sm">{brandName}</Link>}
          <button onClick={() => setOpen(!open)} className="text-stone-400 hover:text-white ml-auto icon-button" aria-label={open ? '사이드바 접기' : '사이드바 펼치'} title={open ? '사이드바 접기' : '사이드바 펼치'}>
            {open ? <X size={18} /> : <Menu size={18} />}
          </button>
        </div>

        {/* 메뉴 */}
        <nav className="flex-1 p-2 space-y-0.5 overflow-y-auto">
          {MENU
            .filter(item => item.roles.includes(user?.role ?? ''))
            .map(item => {
              const Icon = item.icon;
              return (
              <Link
                key={item.to}
                to={item.to}
                title={!open ? item.label : undefined}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition ${
                  isActive(item.to, item.exact)
                    ? 'bg-amber-500 text-black font-semibold'
                    : 'text-stone-400 hover:bg-stone-800 hover:text-white'
                }`}
              >
                <Icon size={18} className="flex-shrink-0" aria-hidden="true" />
                {open && <span>{item.label}</span>}
              </Link>
            )})}
        </nav>

        {/* ── 하단: 사용자 프로필 + 로그아웃 ── */}
        <div className="border-t border-stone-700 p-2 space-y-0.5">

          {/* 내 프로필 링크 */}
          <Link
            to="/admin/profile"
            title={!open ? '내 프로필' : undefined}
            className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition ${
              isActive('/admin/profile')
                ? 'bg-amber-500 text-black font-semibold'
                : 'text-stone-400 hover:bg-stone-800 hover:text-white'
            }`}
          >
            {/* 아바타 */}
            <span className="w-6 h-6 rounded-full bg-amber-500/30 text-amber-300 flex items-center justify-center text-xs font-bold flex-shrink-0">
              {initial}
            </span>
            {open && (
              <div className="min-w-0">
                <p className="text-xs font-semibold text-white truncate">{user?.name}</p>
                <p className="text-xs text-stone-500 truncate">{user?.partner}</p>
              </div>
            )}
          </Link>

          {/* 로그아웃 */}
          <button
            onClick={handleLogout}
            title={!open ? '로그아웃' : undefined}
            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-stone-400 hover:bg-stone-800 hover:text-red-400 transition text-sm"
          >
            <LogOut size={18} className="flex-shrink-0" aria-hidden="true" />
            {open && <span>로그아웃</span>}
          </button>
        </div>
      </aside>

      {/* ── 본문 ── */}
      <main className="flex-1 overflow-auto">
        <Outlet />
      </main>
    </div>
  );
}
